import { first, map, concatMap, catchError, concatAll, tap } from 'rxjs/operators';
import { EMPTY, of, Observable } from 'rxjs';

import { Injectable, Optional, SkipSelf, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { SgLazyAppModule, SgLazyAppConfig } from '../sg-lazy-app';
import { EnvironmentService, EnvironmentLanguage } from './environment.service';
import { currentApp } from 'apps/shell-app/src/app-bootstrap';
import { DateTime } from 'luxon';

/**
 * Sharegain translation service, this wraps the TranslateService and apply sharegain lazy module app logic.
 * Each lazy app module that has internal translations must register a provider for SgTranslationService.
 *
 * The Root module has it's own SgTranslationService instance and each lazy app module (feature module) can have a
 * dedicated instance of SgTranslationService as well.
 *
 * SgTranslationService instance is bound to the module, within the module boundary only 1 instance is allowed, creating
 * multiple instances of SgTranslationService within the same scope of the module's injector will throw.
 */
@Injectable()
export class SgTranslationService implements OnDestroy {
  root: SgTranslationService & { current: SgTranslationService };

  get langs(): EnvironmentLanguage[] {
    return this.root.langs;
  }

  private currentLoader: TranslateLoader;
  private langCache: any = {};
  private moduleConfig: SgLazyAppConfig;

  constructor(
    private env: EnvironmentService,
    public httpClient: HttpClient,
    public translate: TranslateService,
    @Optional()
    @SkipSelf()
    public parent?: SgTranslationService
  ) {
    // follow issue https://github.com/angular/angular/issues/23058 for a fix
  }

  init(module?: SgLazyAppModule) {
    // The first instance of SgTranslationService has a different init process...
    if (!this.parent && !this.translate.currentLang) {
      this.root = <any>this;
      this.initRoot();
    } else {
      if (!module || !module.config || !module.config.name) {
        throw new Error('Invalid module host, only "SgLazyAppModule" modules are allowed.');
      }
      this.moduleConfig = module.config;
      this.root = this.parent.root;
      this.initFeature();
    }
    this.root.current = this;
  }

  ngOnDestroy(): void {
    this.root.current = this.parent;
  }

  changeLang(lang: string): Promise<void> {
    const chain: SgTranslationService[] = [];
    let tmp = <any>this;
    while (tmp.parent) {
      chain.unshift(tmp);
      tmp = tmp.parent;
    }

    return <any>this.root.translate
      .use(lang)
      .pipe(
        first(),
        map(() => chain),
        concatAll(),
        concatMap((t: any) => t.getTranslation(lang).pipe(catchError((e) => EMPTY))),
        tap((trans) => trans && this.translate.setTranslation(lang, trans, true))
      )
      .toPromise();
  }

  private getTranslation(lang: string): Observable<any> {
    if (!this.langCache[lang]) {
      return this.currentLoader.getTranslation(lang).pipe(tap((t) => (this.langCache[lang] = t)));
    } else {
      return of(this.langCache[lang]);
    }
  }

  private initRoot(): void {
    const langs = Object.keys(this.env.i18n.langs).map((k) => this.env.i18n.langs[k]);
    Object.defineProperty(this, 'langs', { value: langs });

    this.currentLoader = this.translate.currentLoader;
    this.translate.setDefaultLang(this.env.i18n.default);
    this.translate.addLangs(Object.keys(this.env.i18n.langs));

    // TODO: persist select lang to local storage and use it instead of default
    this.translate.use(this.env.i18n.default);
  }

  private initFeature(): void {
    const langs = this.moduleConfig.langs;
    if (langs) {
      this.root.langs.splice(0, this.root.langs.length, ...langs.map((k) => this.env.i18n.langs[k]));
    }

    const modulePath = `${this.env.getDeployUrl('/')}assets/${this.moduleConfig.name}`;
    let translationPath = `${modulePath}/i18n/`;
    if (currentApp.branding.name.toUpperCase() === 'CITI' && currentApp.name === 'sg-lender') {
      translationPath = `${modulePath}/i18n-citi/`;
    }

    const version = this.env.meta?.version ?? DateTime.now().toFormat('yy_M_d_Hm');
    this.currentLoader = new TranslateHttpLoader(this.httpClient, translationPath, `.json?v=${version}`);
    const lang = this.translate.currentLang;
    this.getTranslation(lang)
      .toPromise()
      .then((t) => this.translate.setTranslation(lang, t, true));
  }
}
